import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CircleCheck } from "lucide-react";
import Marquee from "react-fast-marquee";
import { AnimatePresence, motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/common/Reveal";
import { athletes, caseStudies, partnerLogos, processSteps, services } from "@/data/siteContent";

const opportunityPoints = [
  "Fragmented ecosystem across athletes, IPs, creators, and communities.",
  "Wrong creator fit and low authenticity in performance categories.",
  "No structured execution backbone for sports + wellness campaigns.",
];

const excludedHeroAthleteNames = new Set([
  "Lokendra Singh",
  "Akashdeep Singh",
  "Davinder Singh",
  "Shikha and Ashwini",
  "Ritika Thakur",
]);

const heroImageOverrides = {
  "Menika Thakur": "https://customer-assets.emergentagent.com/job_athlete-hub-88/artifacts/aikigeoj_Menika.PNG",
  "Sakshi Chavan": "https://customer-assets.emergentagent.com/job_athlete-hub-88/artifacts/5fbjvq5t_Sakshi.JPG",
  "Prashant Singh Rawat": "https://customer-assets.emergentagent.com/job_athlete-hub-88/artifacts/8azfhd1y_Prashant.jpeg",
  "Shiya Lalwani": "https://customer-assets.emergentagent.com/job_athlete-hub-88/artifacts/8jxxxmtz_Shiya.jpeg",
  "Sahil Jadhav": "https://customer-assets.emergentagent.com/job_athlete-hub-88/artifacts/sozttdxn_sahil.JPG",
  "Manan Vohra": "https://customer-assets.emergentagent.com/job_athlete-hub-88/artifacts/ofzq1pmy_Manan.PNG",
  "Deyan Shah": "https://customer-assets.emergentagent.com/job_athlete-hub-88/artifacts/jpn28pg9_Deyan.jpeg",
  "Sehnaj Singh": "https://customer-assets.emergentagent.com/job_athlete-hub-88/artifacts/3ewi9suz_sehnaj.png",
  "Lamine Moro": "https://customer-assets.emergentagent.com/job_athlete-hub-88/artifacts/l8ebs5lc_lamine.png",
  "Gurmukh Singh": "https://customer-assets.emergentagent.com/job_athlete-hub-88/artifacts/1mv0ig3n_Gurmukh.png",
  "Yashita Bakshi": "https://customer-assets.emergentagent.com/job_athlete-hub-88/artifacts/obzmmjwm_WhatsApp%20Image%202026-07-02%20at%203.26.02%20PM.jpeg",
};

export default function HomePage() {
  const rotatingHeroImages = useMemo(() => {
    const athleteImages = athletes
      .filter((athlete) => !excludedHeroAthleteNames.has(athlete.name))
      .map((athlete) => heroImageOverrides[athlete.name] || athlete.image)
      .filter((image) => Boolean(image && image.trim()));

    return athleteImages.length > 0
      ? athleteImages
      : ["https://images.pexels.com/photos/19787364/pexels-photo-19787364.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400"];
  }, []);

  const [heroImageIndex, setHeroImageIndex] = useState(0);

  useEffect(() => {
    if (rotatingHeroImages.length <= 1) {
      return undefined;
    }

    const interval = setInterval(() => {
      setHeroImageIndex((prev) => (prev + 1) % rotatingHeroImages.length);
    }, 4600);

    return () => clearInterval(interval);
  }, [rotatingHeroImages]);

  const currentHeroImageSrc = rotatingHeroImages[heroImageIndex];
  const currentHeroImagePosition = currentHeroImageSrc?.includes("INDIA%2C%20your%20new%20fastest%20man%20alive")
    ? "50% 12%"
    : "center";

  return (
    <div className="mx-auto w-full max-w-7xl space-y-16 pb-12 sm:space-y-24" data-testid="home-page">
      <section
        className="relative isolate overflow-hidden rounded-3xl border border-blue-200/20"
        data-testid="hero-section"
      >
        <div className="relative h-[78vh] w-full" data-testid="hero-background-wrapper">
          <AnimatePresence mode="wait">
            <motion.img
              key={currentHeroImageSrc}
              src={currentHeroImageSrc}
              alt="Purnam athletes showcase"
              className="absolute inset-0 h-full w-full object-cover object-center"
              style={{ objectPosition: currentHeroImagePosition }}
              initial={{ opacity: 0.15, scale: 1.03 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0.1, scale: 1.01 }}
              transition={{ duration: 1, ease: "easeInOut" }}
              data-testid="hero-background-image"
            />
          </AnimatePresence>
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#02133d]/35 via-[#031a56]/55 to-[#04143d]/92" />

        <div className="absolute inset-0 flex items-end p-6 sm:p-10 lg:p-14">
          <Reveal className="max-w-3xl space-y-6" delay={0.1}>
            <p className="font-oswald text-xs uppercase tracking-[0.28em] text-accent" data-testid="hero-kicker">
              Purnam Talent Management · Sports & Wellness Growth Partner
            </p>
            <div className="flex items-center gap-2" data-testid="hero-tricolor-stripe">
              <span className="h-1 w-12 rounded-full bg-primary" data-testid="hero-tricolor-blue" />
              <span className="h-1 w-8 rounded-full bg-accent" data-testid="hero-tricolor-saffron" />
              <span className="h-1 w-6 rounded-full bg-emerald-400" data-testid="hero-tricolor-green" />
            </div>
            <h1
              className="font-oswald text-4xl uppercase leading-[0.95] tracking-tight text-white sm:text-5xl lg:text-6xl"
              data-testid="hero-headline"
            >
              Where Sport, Wellness & Culture Build Brands
            </h1>
            <p className="max-w-2xl text-sm text-white/85 sm:text-base" data-testid="hero-subheadline">
              India's specialist agency for brands that want to connect with the new generation of active Indians.
            </p>
            <div className="flex flex-wrap gap-4" data-testid="hero-cta-group">
              <Button asChild className="rounded-full bg-primary px-6 font-oswald uppercase tracking-[0.16em] text-white hover:bg-accent hover:text-[#031447]">
                <Link to="/services" data-testid="hero-explore-services-button">
                  Explore Services
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="rounded-full border-blue-100/45 bg-blue-900/35 px-6 font-oswald uppercase tracking-[0.16em] text-white hover:border-accent hover:bg-blue-900/55 hover:text-accent"
              >
                <a href="mailto:inquiries@purnamtalent.com" data-testid="hero-partner-with-us-button">
                  Partner With Us
                </a>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="grid gap-8 md:grid-cols-12" data-testid="opportunity-section">
        <Reveal className="space-y-5 md:col-span-7" delay={0.05}>
          <p className="font-oswald text-xs uppercase tracking-[0.24em] text-accent" data-testid="opportunity-kicker">
            The Opportunity
          </p>
          <h2 className="font-oswald text-3xl uppercase tracking-tight text-white sm:text-4xl" data-testid="opportunity-title">
            India Is Having Its Sports & Wellness Moment. Most Brands Are Missing It.
          </h2>
          <ul className="space-y-3" data-testid="opportunity-points-list">
            {opportunityPoints.map((point, idx) => (
              <li key={point} className="flex items-start gap-3 text-sm text-blue-100/90 sm:text-base" data-testid={`opportunity-point-${idx + 1}`}>
                <CircleCheck size={16} className="mt-1 text-accent" />
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </Reveal>

        <Reveal className="md:col-span-5" delay={0.12}>
          <div className="h-full rounded-2xl border border-blue-100/25 bg-card/80 p-6 backdrop-blur-sm" data-testid="founder-story-card">
            <p className="font-oswald text-xs uppercase tracking-[0.24em] text-primary" data-testid="opportunity-solution-label">
              Why Purnam
            </p>
            <p className="mt-4 text-lg text-white" data-testid="opportunity-solution-text">
              “One structured partner. End-to-end execution. Measurable brand outcomes.”
            </p>
            <Link
              to="/about"
              className="mt-6 inline-flex items-center gap-2 font-oswald text-xs uppercase tracking-[0.16em] text-accent transition-colors hover:text-white"
              data-testid="opportunity-learn-more-link"
            >
              Learn More <ArrowRight size={14} />
            </Link>
          </div>
        </Reveal>
      </section>

      <section className="space-y-8" data-testid="services-preview-section">
        <Reveal>
          <p className="font-oswald text-xs uppercase tracking-[0.24em] text-accent" data-testid="services-preview-kicker">
            Our Verticals
          </p>
          <h2 className="mt-4 font-oswald text-3xl uppercase tracking-tight text-white sm:text-4xl" data-testid="services-preview-heading">
            Integrated Services Across Talent, IP, And Strategy
          </h2>
        </Reveal>
        <div className="grid gap-5 lg:grid-cols-3" data-testid="services-preview-grid">
          {services.map((service, index) => (
            <Reveal key={service.title} delay={index * 0.06}>
              <article className="h-full rounded-2xl border border-blue-100/20 bg-card/75 p-6" data-testid={`home-service-card-${index + 1}`}>
                <h3 className="font-oswald text-2xl uppercase text-white" data-testid={`home-service-title-${index + 1}`}>
                  {service.title}
                </h3>
                <p className="mt-3 text-sm text-blue-100/85" data-testid={`home-service-description-${index + 1}`}>
                  {service.description}
                </p>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="space-y-7" data-testid="case-studies-section">
        <Reveal>
          <p className="font-oswald text-xs uppercase tracking-[0.24em] text-accent" data-testid="case-studies-kicker">
            Case Studies
          </p>
          <h2 className="mt-4 font-oswald text-3xl uppercase tracking-tight text-white sm:text-4xl" data-testid="case-studies-heading">
            Campaigns Built For Trust, Reach, And Performance
          </h2>
        </Reveal>
        <div className="grid gap-5 md:grid-cols-2" data-testid="case-studies-grid">
          {caseStudies.map((study, index) => (
            <Reveal key={study.title} delay={index * 0.08}>
              <article className="h-full rounded-2xl border border-blue-100/20 bg-card/75 p-6" data-testid={`case-study-card-${index + 1}`}>
                <h3 className="font-oswald text-2xl uppercase text-white" data-testid={`case-study-title-${index + 1}`}>
                  {study.title}
                </h3>
                <p className="mt-3 text-sm text-blue-100/85" data-testid={`case-study-summary-${index + 1}`}>
                  {study.summary}
                </p>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="space-y-6" data-testid="trusted-brands-section">
        <Reveal>
          <p className="font-oswald text-xs uppercase tracking-[0.24em] text-accent" data-testid="trusted-brands-kicker">
            Brands That Trust Purnam
          </p>
        </Reveal>
        <Reveal delay={0.08}>
          <div className="rounded-2xl border border-blue-100/20 bg-card/75 py-5" data-testid="trusted-brands-marquee">
            <Marquee gradient={false} speed={40} pauseOnHover>
              <div className="flex items-center gap-12 px-5">
                {partnerLogos.map((brand, index) => (
                  <a
                    href={brand.website}
                    target="_blank"
                    rel="noreferrer"
                    className="font-oswald text-xl uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:text-accent"
                    key={brand.name}
                    data-testid={`trusted-brand-${index + 1}`}
                  >
                    {brand.name}
                  </a>
                ))}
              </div>
            </Marquee>
          </div>
        </Reveal>
      </section>

      <section className="space-y-8" data-testid="how-we-work-section">
        <Reveal>
          <p className="font-oswald text-xs uppercase tracking-[0.24em] text-accent" data-testid="how-we-work-kicker">
            How We Work
          </p>
          <h2 className="mt-4 font-oswald text-3xl uppercase tracking-tight text-white sm:text-4xl" data-testid="how-we-work-heading">
            Structured, Fast, And Built For Outcomes
          </h2>
        </Reveal>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5" data-testid="how-we-work-steps-grid">
          {processSteps.map((step, index) => (
            <Reveal key={step} delay={index * 0.06}>
              <div className="rounded-2xl border border-blue-100/20 bg-card/80 p-4" data-testid={`how-we-work-step-${index + 1}`}>
                <p className="font-oswald text-xs uppercase tracking-[0.2em] text-primary" data-testid={`how-we-work-step-label-${index + 1}`}>
                  Step {index + 1}
                </p>
                <p className="mt-2 text-sm text-white" data-testid={`how-we-work-step-title-${index + 1}`}>{step}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2" data-testid="split-cta-section">
        <Reveal>
          <div className="rounded-2xl border border-blue-100/20 bg-card/75 p-8" data-testid="athlete-cta-card">
            <p className="font-oswald text-xs uppercase tracking-[0.2em] text-accent" data-testid="athlete-cta-kicker">
              For Brands
            </p>
            <h3 className="mt-3 font-oswald text-2xl uppercase text-white" data-testid="athlete-cta-heading">
              Let's Build Something That Matters.
            </h3>
            <Button asChild className="mt-6 rounded-full bg-primary px-6 font-oswald uppercase tracking-[0.16em] text-white hover:bg-accent hover:text-[#031447]">
              <a href="mailto:inquiries@purnamtalent.com" data-testid="athlete-contact-button">Book A Consultation</a>
            </Button>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="rounded-2xl border border-blue-100/20 bg-card/75 p-8" data-testid="brand-cta-card">
            <p className="font-oswald text-xs uppercase tracking-[0.2em] text-primary" data-testid="brand-cta-kicker">
              Contact Details
            </p>
            <h3 className="mt-3 font-oswald text-2xl uppercase text-white" data-testid="brand-cta-heading">
              inquiries@purnamtalent.com
            </h3>
            <p className="mt-4 text-sm text-blue-100/90" data-testid="brand-cta-phone">Reach our team for partnerships and campaigns.</p>
            <a
              href="mailto:inquiries@purnamtalent.com"
              className="mt-6 inline-flex items-center gap-2 font-oswald text-xs uppercase tracking-[0.16em] text-accent transition-colors hover:text-white"
              data-testid="brand-contact-button"
            >
              Email Us <ArrowRight size={14} />
            </a>
          </div>
        </Reveal>
      </section>
    </div>
  );
}